/**
 * The codified-rules view for a guideline version — `structured.json` as served
 * by `GET /admin/data/guidelines/{slug}` and saved back by
 * `PUT /admin/data/guidelines/{slug}/structured`.
 *
 * Shape (guidelines-and-dscr-pricing-admin.md):
 *   rules[]              — section, category, field, operator, value, unit,
 *                          checkType, modelMapping {losField, exists, models}
 *   requiredDocuments[]  ineligible[]  leverageGrids[]
 *   pydanticSuggestions / testSuggestions — python code strings, READ-ONLY (copy)
 *
 * `modelMapping.exists` is code-verified against the live pydantic models, so an
 * `exists:false` row means the guideline references a field the LOS model does
 * not have — that's the actionable signal here and is surfaced as a red chip.
 *
 * The rules grid is inline-editable for the scalar columns; the collection
 * fields (requiredDocuments / ineligible / leverageGrids) round-trip untouched
 * so a save never drops parts of the document we don't render as a form.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import { useSnackbar } from "notistack";

import {
  ADMIN_GRID_COL_DEF,
  AdminGrid,
  ExpandableBlock,
} from "../shared/adminGrid";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import SearchInput from "../../../components/inputs/SearchInput";
import { AdminCard, AdminEmpty } from "../shared/AdminScreenShell";

const CATEGORIES = [
  "quantitative",
  "qualitative",
  "document",
  "restriction",
  "state",
  "exception",
];

const CHECK_TYPES = [
  "hard_fail",
  "soft_exception",
  "leverage_reduction",
  "document_condition",
  "warning",
];

const CHECK_TYPE_COLORS = {
  hard_fail: { bg: "#fee2e2", fg: "#991b1b" },
  soft_exception: { bg: "#fef3c7", fg: "#92400e" },
  leverage_reduction: { bg: "#e0e7ff", fg: "#3730a3" },
  document_condition: { bg: "#e0f2fe", fg: "#075985" },
  warning: { bg: "#f1f5f9", fg: "#475569" },
};

export default function GuidelineRulesPanel({ structured, readOnly, onSave }) {
  const { enqueueSnackbar } = useSnackbar();
  const [rules, setRules] = useState([]);
  const [note, setNote] = useState("");
  const [quickFilter, setQuickFilter] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setRules(
      (structured?.rules || []).map((r, i) => ({ ...r, __rowId: String(i) })),
    );
    setDirty(false);
    setNote("");
  }, [structured]);

  const unmappedCount = useMemo(
    () => rules.filter((r) => r.modelMapping && r.modelMapping.exists === false).length,
    [rules],
  );

  const handleCellChanged = useCallback(() => setDirty(true), []);

  const handleSave = useCallback(() => {
    // Strip the grid-only row id and put the edited rules back into the full
    // structured document so untouched collections survive the round trip.
    const next = {
      ...(structured || {}),
      rules: rules.map(({ __rowId, ...rest }) => rest),
    };
    onSave?.(next, note || "rules edited").catch(() => {});
  }, [structured, rules, note, onSave]);

  const copy = useCallback(
    (text, label) => {
      navigator.clipboard
        ?.writeText(text || "")
        .then(() => enqueueSnackbar(`${label} copied`, { variant: "success" }))
        .catch(() =>
          enqueueSnackbar("Couldn't copy to clipboard", { variant: "error" }),
        );
    },
    [enqueueSnackbar],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Section",
        field: "section",
        minWidth: 150,
        flex: 1,
        editable: !readOnly,
      },
      {
        headerName: "Category",
        field: "category",
        width: 140,
        editable: !readOnly,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: CATEGORIES },
      },
      {
        headerName: "Field",
        field: "field",
        minWidth: 170,
        flex: 1,
        editable: !readOnly,
      },
      { headerName: "Op", field: "operator", width: 90, editable: !readOnly },
      {
        headerName: "Value",
        field: "value",
        minWidth: 130,
        flex: 1,
        editable: !readOnly,
        valueFormatter: (p) =>
          p.value == null
            ? "—"
            : typeof p.value === "object"
              ? JSON.stringify(p.value)
              : String(p.value),
      },
      { headerName: "Unit", field: "unit", width: 90, editable: !readOnly },
      {
        headerName: "Check",
        field: "checkType",
        width: 175,
        editable: !readOnly,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: CHECK_TYPES },
        cellRenderer: (p) => {
          if (!p.value) return "—";
          const c = CHECK_TYPE_COLORS[p.value] || CHECK_TYPE_COLORS.warning;
          return (
            <Chip
              size="small"
              label={String(p.value).replace(/_/g, " ")}
              sx={{
                bgcolor: c.bg,
                color: c.fg,
                fontWeight: 600,
                fontSize: 11,
                height: 20,
              }}
            />
          );
        },
      },
      {
        headerName: "LOS field",
        colId: "losField",
        minWidth: 220,
        flex: 1.2,
        valueGetter: (p) => p.data?.modelMapping?.losField || "",
        cellRenderer: (p) => {
          const mm = p.data?.modelMapping;
          if (!mm?.losField)
            return <Txt sx={{ fontSize: 12, color: "#94a3b8" }}>—</Txt>;
          return (
            <Flx ac g={0.75}>
              <Txt sx={{ fontSize: 12, color: "#0f172a" }}>{mm.losField}</Txt>
              {mm.exists === false ? (
                <Chip
                  size="small"
                  label="not on model"
                  sx={{
                    bgcolor: "#fee2e2",
                    color: "#991b1b",
                    fontWeight: 700,
                    fontSize: 10.5,
                    height: 18,
                  }}
                />
              ) : null}
            </Flx>
          );
        },
      },
    ],
    [readOnly],
  );


  const getRowId = useCallback((p) => p.data.__rowId, []);

  if (!structured) {
    return (
      <AdminCard title="Codified rules">
        <AdminEmpty>
          This version has no structured.json. Run "Re-codify" to generate the
          rules index from the current markdown.
        </AdminEmpty>
      </AdminCard>
    );
  }

  return (
    <>
      <AdminCard
        title={`Rules (${rules.length})`}
        endContent={
          <Flx ac g={1} wrap>
            {unmappedCount ? (
              <Chip
                size="small"
                label={`${unmappedCount} field${unmappedCount === 1 ? "" : "s"} not on the LOS model`}
                sx={{
                  bgcolor: "#fee2e2",
                  color: "#991b1b",
                  fontWeight: 700,
                  fontSize: 11,
                  height: 22,
                }}
              />
            ) : null}
            <SearchInput
              value={quickFilter}
              onChange={setQuickFilter}
              placeholder="Filter rules…"
            />
          </Flx>
        }
        dense
      >
        <AdminGrid rowCount={(rules || []).length} maxRows={12}>
          <AgGridReact
            rowData={rules}
            columnDefs={columnDefs}
            defaultColDef={ADMIN_GRID_COL_DEF}
            getRowId={getRowId}
            quickFilterText={quickFilter}
            onCellValueChanged={handleCellChanged}
            stopEditingWhenCellsLoseFocus
          />
        </AdminGrid>
        {readOnly ? null : (
          <Flx ac g={1.5} sx={{ p: 2 }} wrap>
            <TextField
              size="small"
              label="Change note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              sx={{ minWidth: 280, flex: 1 }}
            />
            <Button variant="contained" onClick={handleSave} disabled={!dirty}>
              Save rules as a new version
            </Button>
          </Flx>
        )}
      </AdminCard>

      <ListCard title="Required documents" items={structured.requiredDocuments} />
      <ListCard title="Ineligible" items={structured.ineligible} />

      {structured.leverageGrids?.length ? (
        <AdminCard title={`Leverage grids (${structured.leverageGrids.length})`}>
          <JsonBlock value={structured.leverageGrids} />
        </AdminCard>
      ) : null}

      {structured.pydanticSuggestions ? (
        <AdminCard
          title="Pydantic suggestions (read-only)"
          endContent={
            <Button
              size="small"
              variant="text"
              startIcon={<ContentCopyOutlined sx={{ fontSize: 16 }} />}
              onClick={() =>
                copy(codeText(structured.pydanticSuggestions), "Pydantic suggestions")
              }
            >
              Copy
            </Button>
          }
        >
          <CodeBlock value={structured.pydanticSuggestions} />
        </AdminCard>
      ) : null}

      {structured.testSuggestions ? (
        <AdminCard
          title="Test suggestions (read-only)"
          endContent={
            <Button
              size="small"
              variant="text"
              startIcon={<ContentCopyOutlined sx={{ fontSize: 16 }} />}
              onClick={() =>
                copy(codeText(structured.testSuggestions), "Test suggestions")
              }
            >
              Copy
            </Button>
          }
        >
          <CodeBlock value={structured.testSuggestions} />
        </AdminCard>
      ) : null}
    </>
  );
}

GuidelineRulesPanel.propTypes = {
  structured: PropTypes.object,
  readOnly: PropTypes.bool,
  onSave: PropTypes.func,
};

// The agent emits these either as a single python string or as a list of them.
const codeText = (v) => (Array.isArray(v) ? v.join("\n\n") : String(v ?? ""));

const CodeBlock = ({ value }) => (
  <ExpandableBlock
    collapsedMaxHeight={380}
    label="Show all"
    surface="#0f172a"
    color="#94a3b8"
  >
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        bgcolor: "#0f172a",
        color: "#e2e8f0",
        borderRadius: "8px",
        fontSize: 11.5,
        lineHeight: 1.6,
      }}
    >
      {codeText(value)}
    </Box>
  </ExpandableBlock>
);

const JsonBlock = ({ value }) => (
  <ExpandableBlock collapsedMaxHeight={380} label="Show all" surface="#f8fafc">
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        bgcolor: "#f8fafc",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        fontSize: 11.5,
        lineHeight: 1.6,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </Box>
  </ExpandableBlock>
);

const ListCard = ({ title, items }) => {
  if (!items?.length) return null;
  return (
    <AdminCard title={`${title} (${items.length})`}>
      <Flx column g={0.75}>
        {items.map((item, i) => (
          <Txt
            key={i}
            sx={{ fontSize: 12.5, color: "#334155", display: "block" }}
          >
            •{" "}
            {typeof item === "string"
              ? item
              : item?.name || item?.document || item?.reason || JSON.stringify(item)}
          </Txt>
        ))}
      </Flx>
    </AdminCard>
  );
};
